import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { publicApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  CalendarIcon, Users, Phone, User, Clock, ChevronRight,
  Zap, MessageCircle, ArrowLeft, CheckCircle2, Loader2, Mail,
} from 'lucide-react';
import { format } from 'date-fns';

// ── IST helpers ────────────────────────────────────────────────────────────
function getISTDateString() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date()); // returns YYYY-MM-DD
}

function getISTTimeString() {
  const raw = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date()); // returns HH:MM
  return raw.replace('.', ':'); // Safari safety
}

function parseISTDate(dateStr) {
  // Parse a YYYY-MM-DD string as a local-midnight date (avoids UTC offset shifting)
  if (!dateStr) return new Date();
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function safeFmt(dateStr, fmtStr = 'EEE, dd MMM yyyy') {
  try {
    return format(parseISTDate(dateStr), fmtStr);
  } catch {
    return dateStr || '';
  }
}

// ── Component ─────────────────────────────────────────────────────────────
export default function CheckInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceFromQR = searchParams.get('source') || '';

  const [step, setStep] = useState(1); // 1 = phone, 1.5 = OTP, 2 = details

  // Step 1
  const [phone, setPhone] = useState('');
  const [looking, setLooking] = useState(false);
  const [returning, setReturning] = useState(false);
  const phoneRef = useRef(null);

  // Step 1.5: OTP Verification
  const [otpSent, setOtpSent] = useState(false);
  const [otpChannel, setOtpChannel] = useState('');
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canShowFallback, setCanShowFallback] = useState(false);
  const [sendingFallback, setSendingFallback] = useState(false);

  // Step 2
  const [form, setForm] = useState({
    customer_name: '',
    age: '',                      // mandatory; integer ≥ 21 (alcohol license)
    guests: 1,
    visit_date: getISTDateString(),
    arrival_time: getISTTimeString(),
    source: sourceFromQR,
    marketing_opt_in: true,       // MANDATORY for check-in — defaulted ON, unchecking blocks submit
    p_code: '',                   // Optional 3-digit promoter code (walk-in attribution)
  });
  const [selectedDate, setSelectedDate] = useState(parseISTDate(getISTDateString()));
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Auto-focus phone on mount
  useEffect(() => { phoneRef.current?.focus(); }, []);

  // ── SMS Fallback countdown ──────────────────────────────────────────────
  // After WhatsApp OTP is sent, run a 30-second ticker. Once it hits zero,
  // reveal the "Send via SMS" fallback button. The countdown is reset on every
  // new OTP send (initial + manual fallback) by setCountdown(30) elsewhere.
  useEffect(() => {
    if (!otpSent || otpChannel !== 'whatsapp' || canShowFallback) return;
    if (countdown <= 0) {
      setCanShowFallback(true);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpSent, otpChannel, countdown, canShowFallback]);

  // ── Pricing fetch ───────────────────────────────────────────────────────
  const fetchPricing = useCallback(async () => {
    if (!form.visit_date || form.guests < 1) return;
    setPricingLoading(true);
    try {
      const res = await publicApi.calculatePricing(form.visit_date, form.guests);
      setPricing(res.data);
    } catch {
      setPricing(null);
    } finally {
      setPricingLoading(false);
    }
  }, [form.visit_date, form.guests]);

  useEffect(() => {
    if (step === 2) {
      const t = setTimeout(fetchPricing, 300);
      return () => clearTimeout(t);
    }
  }, [fetchPricing, step]);

  // ── Step 1: phone submit & send OTP ────────────────────────────────────
  const handlePhoneContinue = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\s/g, '');
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      setErrors({ phone: 'Enter a valid 10-digit mobile number' });
      return;
    }
    setErrors({});
    setSendingOtp(true);
    
    try {
      // Send OTP (WhatsApp-first with SMS fallback)
      const otpRes = await publicApi.sendOTPSmart('+91' + cleaned);
      const otpData = otpRes.data;
      
      setOtpSent(true);
      setOtpChannel(otpData.channel);
      setCountdown(30);
      setCanShowFallback(false);
      
      if (otpData.channel === 'whatsapp') {
        toast.success('🎉 OTP sent to your WhatsApp!', {
          description: 'Check your WhatsApp for the verification code'
        });
      } else {
        toast.success('📱 OTP sent via SMS', {
          description: 'Check your messages for the verification code'
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(message);
    } finally {
      setSendingOtp(false);
    }
  };

  // ── Step 1.5: SMS Fallback ──────────────────────────────────────────────
  const handleSMSFallback = async () => {
    const cleaned = phone.replace(/\s/g, '');
    setSendingFallback(true);
    try {
      await publicApi.sendOTPSMSFallback('+91' + cleaned);
      setOtpChannel('sms');
      setCountdown(30);
      setCanShowFallback(false);
      toast.success('📱 OTP sent via SMS', {
        description: 'Check your messages for the verification code'
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send SMS';
      toast.error(message);
    } finally {
      setSendingFallback(false);
    }
  };

  // ── Step 1.5: Verify OTP & lookup customer ─────────────────────────────
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }

    setVerifying(true);
    try {
      const cleaned = phone.replace(/\s/g, '');
      await publicApi.verifyOTP('+91' + cleaned, otp);
      
      toast.success('✅ Verified successfully!');
      
      // Now lookup customer after OTP verification
      setLooking(true);
      try {
        const res = await publicApi.lookupCustomer(cleaned);
        const data = res.data;
        if (data.found) {
          setReturning(true);
          setForm(f => ({
            ...f,
            customer_name: data.customer_name || '',
            // Prefill age too — they can still edit, but typically just confirms
            age: data.age != null ? String(data.age) : '',
          }));
        } else {
          setReturning(false);
          setForm(f => ({ ...f, customer_name: '', age: '' }));
        }
      } catch {
        setReturning(false);
      } finally {
        setLooking(false);
      }
      
      // Move to step 2
      setStep(2);
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired OTP';
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  // ── Step 2: validate + submit ──────────────────────────────────────────
  const validate = () => {
    const errs = {};
    const trimmedName = (form.customer_name || '').trim();
    if (!trimmedName || trimmedName.length < 2) {
      errs.customer_name = 'Please enter your full name';
    }
    const ageNum = parseInt(form.age, 10);
    if (!form.age || Number.isNaN(ageNum)) {
      errs.age = 'Age is required';
    } else if (ageNum < 21) {
      errs.age = '21+ entry only — sorry, we cannot allow this booking.';
    } else if (ageNum > 99) {
      errs.age = 'Please enter a valid age';
    }
    if (form.guests < 1 || form.guests > 20) errs.guests = 'Guests must be 1–20';
    if (!form.visit_date) errs.visit_date = 'Visit date is required';
    if (!form.marketing_opt_in) {
      errs.marketing_opt_in = 'WhatsApp updates consent is required to complete check-in.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // Toast the most actionable error first (in order of severity)
      if (!form.marketing_opt_in) {
        toast.error('WhatsApp updates consent is required to complete check-in.');
      }
      return;
    }
    setSubmitting(true);
    try {
      const phoneClean = phone.replace(/\s/g, '');
      const payload = {
        customer_name: form.customer_name.trim(),
        age: parseInt(form.age, 10),
        phone: phoneClean,
        // WhatsApp is now always-on with the login phone — no toggle on the form.
        whatsapp_number:    phoneClean,
        whatsapp_preferred: true,
        guests: parseInt(form.guests),
        visit_date: form.visit_date,
        arrival_time: form.arrival_time || null,
        notes: null,
        source: form.source || null,
        terms_accepted:   true,   // Implicit on submit (form shows inline disclaimer)
        marketing_opt_in: form.marketing_opt_in,
        p_code:           (form.p_code || '').trim() || null,   // Optional 3-digit promoter code
      };
      const res = await publicApi.createCheckIn(payload);
      const checkin = res.data;

      // Pass membership signal forward so SuccessPage can show the "You're now a Scratch member" banner
      const memberQuery = checkin.customer_status === 'new' ? '&new_member=1' : '';

      if (checkin.total_amount > 0) {
        const orderRes = await publicApi.createPaymentOrder({
          checkin_id: checkin._id,
          amount: checkin.total_amount,
        });
        navigate(`/checkin/pay?order_id=${orderRes.data.order_id}&ref=${checkin.reference_id}${memberQuery}`);
      } else {
        navigate(`/checkin/success?ref=${checkin.reference_id}&free=true${memberQuery}`);
      }
    } catch (err) {
      // Backend may return either a plain string OR a structured {code, message} object.
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Something went wrong. Please try again.');
      toast.error(msg);
      // If the failure is specifically a P-code error, surface inline on the field too
      const errCode = typeof detail === 'object' ? detail?.code : null;
      if (errCode && ['INVALID_P_CODE', 'P_CODE_NOT_FOUND', 'P_CODE_INACTIVE', 'PROMOTER_CONFLICT'].includes(errCode)) {
        setErrors(prev => ({ ...prev, p_code: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    try {
      setForm(f => ({ ...f, visit_date: format(date, 'yyyy-MM-dd') }));
    } catch {
      setForm(f => ({ ...f, visit_date: date.toISOString().split('T')[0] }));
    }
    setCalendarOpen(false);
  };

  const totalAmount = pricing?.total || 0;
  const hasCharge = totalAmount > 0;

  // ── STEP 1: Phone Input ────────────────────────────────────────────────
  if (step === 1 && !otpSent) {
    return (
      <div className="min-h-screen hero-gradient flex flex-col" style={{ backgroundColor: '#0A0A0A' }}>
        {/* Brand */}
        <div className="max-w-[440px] mx-auto w-full px-4 pt-10 pb-4">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-7 h-7 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={13} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-lg font-semibold">Scratch Unleashed</span>
          </div>

          <h1 className="font-['EB_Garamond'] text-[2.4rem] leading-tight text-[#F2F2F2] mb-2">
            Welcome.<br />Let's get you in.
          </h1>
          <p className="text-[#B9B9B9] text-sm mb-10">
            Enter your mobile number to begin check-in.
          </p>

          <form onSubmit={handlePhoneContinue} data-testid="phone-step-form">
            <div className="space-y-2 mb-6">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <Phone size={11} /> Mobile Number
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6F6F6F] text-sm font-medium pointer-events-none">
                  +91
                </span>
                <Input
                  ref={phoneRef}
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  data-testid="checkin-phone-input"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={e => {
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setErrors({});
                  }}
                  className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#3A3A3A] h-14 pl-12 text-lg tracking-widest text-[#F2F2F2]"
                />
              </div>
              {errors.phone && (
                <p className="text-[#E05252] text-xs animate-fade-in-up">{errors.phone}</p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="phone-continue-button"
              disabled={sendingOtp || phone.length < 10}
              className="w-full h-14 btn-gold rounded-xl font-semibold text-base flex items-center justify-center gap-2"
            >
              {sendingOtp ? (
                <><Loader2 size={18} className="animate-spin" /> Sending OTP...</>
              ) : (
                <>Continue <ChevronRight size={17} /></>
              )}
            </Button>
          </form>

          <p className="text-center text-[#4A4A4A] text-xs mt-8">
            You'll receive an OTP for verification via WhatsApp or SMS
          </p>
        </div>
      </div>
    );
  }

  // ── STEP 1.5: OTP Verification ──────────────────────────────────────────
  if (step === 1 && otpSent) {
    return (
      <div className="min-h-screen hero-gradient flex flex-col" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="max-w-[440px] mx-auto w-full px-4 pt-10 pb-4">
          {/* Brand */}
          <div className="flex items-center gap-2 mb-10">
            <div className="w-7 h-7 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={13} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-lg font-semibold">Scratch Unleashed</span>
          </div>

          <h1 className="font-['EB_Garamond'] text-[2.4rem] leading-tight text-[#F2F2F2] mb-2">
            Verify your number
          </h1>
          <p className="text-[#B9B9B9] text-sm mb-6">
            Enter the 6-digit code sent to +91 {phone}
          </p>

          {/* Channel Info Box */}
          <div className="p-4 rounded-xl mb-6" style={{
            background: otpChannel === 'whatsapp' ? 'rgba(37,211,102,0.08)' : 'rgba(212,168,67,0.08)',
            border: `1px solid ${otpChannel === 'whatsapp' ? 'rgba(37,211,102,0.25)' : 'rgba(212,168,67,0.25)'}`
          }}>
            <div className="flex items-center gap-2 mb-1">
              {otpChannel === 'whatsapp' ? (
                <MessageCircle size={16} style={{ color: '#25D366' }} />
              ) : (
                <Mail size={16} style={{ color: '#D4A843' }} />
              )}
              <span className="font-semibold text-sm" style={{ color: otpChannel === 'whatsapp' ? '#25D366' : '#D4A843' }}>
                OTP sent via {otpChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}
              </span>
              <CheckCircle2 size={14} style={{ color: otpChannel === 'whatsapp' ? '#25D366' : '#D4A843' }} />
            </div>
            <p className="text-xs text-[#B9B9B9]">
              {otpChannel === 'whatsapp' 
                ? 'Check your WhatsApp messages'
                : 'Check your SMS inbox'}
            </p>
          </div>

          {/* OTP Input Form */}
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <Label className="text-[#B9B9B9] text-xs mb-2 block">Verification Code</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#3A3A3A] h-14 text-center text-2xl tracking-widest text-[#F2F2F2]"
                autoFocus
                disabled={verifying}
              />
            </div>

            <Button
              type="submit"
              disabled={verifying || otp.length !== 6}
              className="w-full h-14 btn-gold rounded-xl font-semibold text-base"
            >
              {verifying ? (
                <><Loader2 size={18} className="animate-spin" /> Verifying...</>
              ) : (
                'Verify & Continue'
              )}
            </Button>
          </form>

          {/* SMS Fallback */}
          {otpChannel === 'whatsapp' && !canShowFallback && (
            <div className="text-center p-3 rounded-lg bg-[#111111] mt-4">
              <p className="text-xs text-[#B9B9B9]">
                SMS fallback available in <span className="text-[#D4A843] font-semibold">{countdown}s</span>
              </p>
            </div>
          )}

          {canShowFallback && (
            <Button
              variant="outline"
              className="w-full mt-4 h-12 border-[#2A2A2A] text-[#B9B9B9] hover:text-[#F2F2F2] hover:border-[#D4A843]/40"
              onClick={handleSMSFallback}
              disabled={sendingFallback}
            >
              {sendingFallback ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending SMS...</>
              ) : (
                <><Mail className="mr-2 h-4 w-4" /> Didn't receive? Send via SMS</>
              )}
            </Button>
          )}

          {/* Change Number */}
          <button
            onClick={() => {
              setOtpSent(false);
              setOtp('');
              setCountdown(30);
              setCanShowFallback(false);
            }}
            className="w-full text-sm text-[#6F6F6F] hover:text-[#B9B9B9] transition-colors mt-6 text-center"
          >
            Change phone number
          </button>
        </div>
      </div>
    );
  }

  // ── STEP 2: Check-in Details ───────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
      {/* Header */}
      <div className="hero-gradient">
        <div className="max-w-[440px] mx-auto px-4 pt-6 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => { setStep(1); setErrors({}); }}
              className="w-8 h-8 rounded-full bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[#B9B9B9] hover:text-[#F2F2F2] hover:border-[#D4A843]/40 transition-colors"
              data-testid="back-to-step1"
            >
              <ArrowLeft size={14} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
                <Zap size={11} className="text-[#120F08]" />
              </div>
              <span className="font-['EB_Garamond'] text-[#F2F2F2] text-base font-semibold">Scratch Unleashed</span>
            </div>
          </div>

          {/* Phone badge + returning greeting */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4A843]/15 border border-[#D4A843]/35 flex items-center justify-center flex-shrink-0">
              <Phone size={16} className="text-[#D4A843]" />
            </div>
            <div>
              <p className="text-[#F2F2F2] font-semibold text-sm">+91 {phone}</p>
              {returning ? (
                <div className="flex items-center gap-1 mt-0.5">
                  <CheckCircle2 size={11} className="text-[#2FBF71]" />
                  <p className="text-[#2FBF71] text-xs">
                    Welcome back{form.customer_name ? `, ${form.customer_name}` : ''}!
                  </p>
                </div>
              ) : (
                <p className="text-[#B9B9B9] text-xs">
                  Just a couple of taps — we'll set up your account automatically.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form
        id="checkin-form"
        onSubmit={handleSubmit}
        data-testid="checkin-form"
        className="max-w-[440px] mx-auto px-4 pb-32 space-y-3 mt-3"
      >
        {/* Returning member badge — keeps the warm welcome up top */}
        {returning && form.customer_name && (
          <div
            data-testid="returning-member-card"
            className="surface-card rounded-xl p-3 flex items-center gap-3 border border-[#2FBF71]/20 bg-[#2FBF71]/[0.04]"
          >
            <div className="w-9 h-9 rounded-full bg-[#2FBF71]/15 border border-[#2FBF71]/30 flex items-center justify-center flex-shrink-0">
              <User size={14} className="text-[#2FBF71]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-[#2FBF71] uppercase tracking-widest font-semibold">Returning member</p>
              <p className="text-[#F2F2F2] text-sm font-semibold truncate">{form.customer_name}</p>
            </div>
            <p className="text-[10px] text-[#6F6F6F]">Edit below if needed</p>
          </div>
        )}

        {/* Identity Card — Name + Age (both mandatory) */}
        <div className="surface-card card-highlight rounded-xl p-4 space-y-4">
          <p className="text-xs text-[#D4A843] uppercase tracking-wider font-semibold">Your Details</p>

          <div className="space-y-1.5">
            <Label htmlFor="customer_name" className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
              <User size={11} /> Full Name
            </Label>
            <Input
              id="customer_name"
              data-testid="checkin-name-input"
              placeholder="Enter your full name"
              autoCapitalize="words"
              autoComplete="name"
              value={form.customer_name}
              onChange={e => {
                setForm(f => ({ ...f, customer_name: e.target.value }));
                if (errors.customer_name) {
                  setErrors(prev => { const { customer_name: _o, ...rest } = prev; return rest; });
                }
              }}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-11"
            />
            {errors.customer_name && (
              <p data-testid="checkin-name-error" className="text-[#E05252] text-xs animate-fade-in-up">{errors.customer_name}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="age" className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
              <CalendarIcon size={11} /> Age
              <span className="text-[#6F6F6F]">· 21+ entry only</span>
            </Label>
            <Input
              id="age"
              type="number"
              inputMode="numeric"
              min={21}
              max={99}
              maxLength={2}
              data-testid="checkin-age-input"
              placeholder="e.g. 24"
              value={form.age}
              onChange={e => {
                // Strip non-digits, cap at 2 chars so the number wheel doesn't get silly
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                setForm(f => ({ ...f, age: v }));
                if (errors.age) {
                  setErrors(prev => { const { age: _o, ...rest } = prev; return rest; });
                }
              }}
              className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-11 max-w-[140px]"
            />
            {errors.age && (
              <p data-testid="checkin-age-error" className="text-[#E05252] text-xs animate-fade-in-up">{errors.age}</p>
            )}
          </div>
        </div>

        {/* Visit Details Card */}
        <div className="surface-card card-highlight rounded-xl p-4 space-y-4">
          <p className="text-xs text-[#D4A843] uppercase tracking-wider font-semibold">Visit Details</p>

          <div className="grid grid-cols-2 gap-3">
            {/* Guests */}
            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <Users size={11} /> Guests
              </Label>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={20}
                data-testid="checkin-guests-input"
                value={form.guests}
                onChange={e => {
                  const n = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                  setForm(f => ({ ...f, guests: n }));
                }}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11"
              />
              {errors.guests && <p className="text-[#E05252] text-xs">{errors.guests}</p>}
            </div>

            {/* Arrival Time — pre-filled IST */}
            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <Clock size={11} /> Arrival Time
              </Label>
              <Input
                type="time"
                value={form.arrival_time}
                onChange={e => setForm(f => ({ ...f, arrival_time: e.target.value }))}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11"
              />
            </div>
          </div>

          {/* Visit Date — pre-filled IST */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
              <CalendarIcon size={11} /> Visit Date
            </Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="checkin-visit-date-button"
                  className="w-full justify-start text-left bg-[#111111] border-[#2A2A2A] hover:border-[#D4A843]/40 hover:bg-[#111111] text-[#F2F2F2] h-11"
                >
                  <CalendarIcon size={13} className="mr-2 text-[#D4A843]" />
                  {form.visit_date ? safeFmt(form.visit_date, 'EEE, dd MMM yyyy') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#2A2A2A]">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  initialFocus
                  className="text-[#F2F2F2]"
                />
              </PopoverContent>
            </Popover>
            {errors.visit_date && <p className="text-[#E05252] text-xs">{errors.visit_date}</p>}
          </div>

          {/* P-code — optional 3-digit promoter code */}
          <div className="space-y-1.5">
            <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
              <span className="font-mono text-[#D4A843] text-[10px] border border-[#D4A843]/30 rounded px-1 py-px">P</span>
              P-code <span className="text-[#6F6F6F] text-[10px] normal-case">(Optional · 3-digit)</span>
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={3}
              placeholder="e.g., 247"
              data-testid="checkin-pcode-input"
              value={form.p_code}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                setForm(f => ({ ...f, p_code: v }));
                if (errors.p_code) setErrors(prev => ({ ...prev, p_code: undefined }));
              }}
              className={`bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-11 tracking-[3px] font-mono ${
                errors.p_code ? 'border-[#E05252]/60' : ''
              }`}
            />
            {errors.p_code ? (
              <p className="text-[#E05252] text-xs">{errors.p_code}</p>
            ) : (
              <p className="text-[#6F6F6F] text-[10px]">
                Came in through a promoter? Enter their 3-digit code so they get credit.
              </p>
            )}
          </div>
        </div>

        {/* Pricing Summary Card */}
        <div data-testid="pricing-summary-card" className="surface-card card-highlight rounded-xl p-4 border border-[#2A2A2A]">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#D4A843] uppercase tracking-wider font-semibold">Cover Charge</p>
            {pricingLoading && (
              <div className="w-4 h-4 border border-[#D4A843] border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          {pricing ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#B9B9B9]">Date</span>
                <span className="text-[#F2F2F2]">{safeFmt(form.visit_date, 'EEE, dd MMM yyyy')}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#B9B9B9]">Guests</span>
                <span className="text-[#F2F2F2]">{form.guests}</span>
              </div>
              {pricing.rule && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#B9B9B9]">Event</span>
                  <span className="text-[#F2F2F2]">{pricing.rule.event_title || 'Regular Night'}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#B9B9B9]">Breakdown</span>
                <span className="text-[#D4A843] text-xs">{pricing.breakdown}</span>
              </div>
              <div className="border-t border-[#2A2A2A] pt-2 mt-2 flex justify-between items-center">
                <span className="text-[#F2F2F2] font-semibold">Total Amount</span>
                <span className="font-['EB_Garamond'] text-2xl text-[#D4A843] font-semibold">
                  {hasCharge ? formatCurrency(totalAmount) : 'FREE'}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="h-4 w-full bg-[#202020] rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-[#1A1A1A] rounded animate-pulse" />
            </div>
          )}
        </div>

        {/* WhatsApp Marketing Consent — MANDATORY, replaces the old Terms checkbox.
            Required so we can send MARKETING-category templates without Meta
            silently dropping them (e.g. promo blasts, event invites). */}
        <div
          data-testid="checkin-consent-card"
          className="surface-card card-highlight rounded-xl p-4 border transition-colors"
          style={{
            borderColor: form.marketing_opt_in
              ? 'rgba(37,211,102,0.35)'
              : (errors.marketing_opt_in ? 'rgba(224,82,82,0.45)' : '#2A2A2A')
          }}
        >
          <label
            htmlFor="marketing_opt_in"
            className="flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              id="marketing_opt_in"
              type="checkbox"
              data-testid="checkin-marketing-checkbox"
              checked={form.marketing_opt_in}
              onChange={e => {
                setForm(f => ({ ...f, marketing_opt_in: e.target.checked }));
                if (e.target.checked) {
                  setErrors(prev => {
                    const { marketing_opt_in: _omit, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              className="mt-0.5 w-4 h-4 accent-[#25D366] cursor-pointer flex-shrink-0"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-1">
                <MessageCircle
                  size={12}
                  style={{ color: form.marketing_opt_in ? '#25D366' : '#6F6F6F' }}
                />
                <span className="text-[#F2F2F2] text-sm font-semibold">
                  Send me WhatsApp updates
                </span>
              </div>
              <p className="text-[#B9B9B9] text-xs leading-relaxed">
                Booking confirmations, event invites, and members-only offers — straight on WhatsApp at <span className="text-[#F2F2F2] font-medium">+91 {phone}</span>.
              </p>
            </div>
          </label>
          {errors.marketing_opt_in && (
            <p data-testid="checkin-marketing-error" className="text-[#E05252] text-xs mt-2 ml-7 animate-fade-in-up">
              {errors.marketing_opt_in}
            </p>
          )}
        </div>

        {/* Inline Terms & Privacy disclaimer — implicit acceptance on submit */}
        <p className="text-center text-[#6F6F6F] text-[10px] leading-relaxed px-2">
          By checking in you agree to Scratch Unleashed's{' '}
          <a
            href="/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="checkin-terms-link"
            className="text-[#B9B9B9] underline hover:text-[#D4A843]"
          >
            Terms &amp; Conditions
          </a>
          {' '}and{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="checkin-privacy-link"
            className="text-[#B9B9B9] underline hover:text-[#D4A843]"
          >
            Privacy Policy
          </a>
          .
        </p>
      </form>

      {/* Sticky CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 border-t border-[#2A2A2A]"
        style={{ backgroundColor: '#0A0A0A', paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
      >
        <div className="max-w-[440px] mx-auto">
          <Button
            type="submit"
            form="checkin-form"
            data-testid="proceed-to-payment-button"
            disabled={
              submitting
              || !form.marketing_opt_in
              || !form.customer_name?.trim()
              || !form.age
              || parseInt(form.age, 10) < 21
            }
            className="w-full h-13 btn-gold rounded-xl font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.98] h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSubmit}
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Processing...</>
            ) : !form.customer_name?.trim() ? (
              <>Enter your name to continue</>
            ) : !form.age || parseInt(form.age, 10) < 21 ? (
              <>21+ entry only</>
            ) : !form.marketing_opt_in ? (
              <>Enable WhatsApp updates to continue</>
            ) : (
              <>{hasCharge ? `Pay ${formatCurrency(totalAmount)}` : 'Confirm Check-in'} <ChevronRight size={16} /></>
            )}
          </Button>
          {hasCharge && (
            <p className="text-center text-[#6F6F6F] text-xs mt-2">
              Powered by Paytm UPI · Secure payment
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
